import classNames from "classnames";
import noop from "lodash/noop";
import range from "lodash/range";
import React, { FunctionComponent, ReactElement, useEffect, useRef, useState } from "react";
import "./style.scss";

interface Props {
  disabled?: boolean;
  maxInput?: number;
  maxQuantity?: number;
  onChange: (quantity: number) => void;
  value?: number;
}

const QuantitySelect: FunctionComponent<Props> = ({
  value = 1,
  disabled = false,
  maxInput = 9,
  maxQuantity = 99,
  onChange = noop,
}): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const quantities = range(1, maxInput + 1);
  const inputEl = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const handleKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const quantityValid = (quantity: number): boolean => {
        return quantity >= 1 && quantity <= maxQuantity;
      };
      const element = event.currentTarget as HTMLInputElement;
      if (event.key === "Enter" && inputEl.current && quantityValid(Number(element.value))) {
        setInputValue(element.value);
        onChange(Number(element.value));
        setIsOpen(false);
      }
    },
    [onChange, inputEl, setIsOpen, maxQuantity]
  );

  const closeSelect = (event: Event) => {
    if (inputEl.current && !inputEl.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    window.addEventListener("click", closeSelect);
    return () => {
      window.removeEventListener("click", closeSelect);
    };
  }, [isOpen, inputEl, onChange]);

  return (
    <div className="QuantitySelect">
      <div
        className={classNames("QuantitySelect-value", {
          "QuantitySelect-value--disabled": disabled,
        })}
        onClick={() => setIsOpen(!isOpen)}
      >
        {value}
      </div>
      {isOpen && (
        <ul className="QuantitySelect-select">
          {quantities.map((num, index) => (
            <li
              className={classNames("QuantitySelect-select-value", {
                "QuantitySelect-select-value--selected": num === value,
              })}
              key={index}
              onClick={() => {
                setInputValue("");
                onChange(num);
              }}
            >
              {num}
            </li>
          ))}
          <li className="QuantitySelect-select-value">
            <input
              className="QuantitySelect-select-customValue"
              type="number"
              placeholder={`${maxInput + 1}+`}
              tabIndex={0}
              max={maxQuantity}
              min={1}
              ref={inputEl}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              required={true}
              onKeyPress={handleKeyPress}
            />
            <div
              className="QuantitySelect-select-confirm"
              onClick={() => onChange(Number(inputValue))}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

export default QuantitySelect;
